import { useState } from "react";
import { Link } from "react-router-dom";
import { Flex, Text, Heading, Card, Button, Callout } from "@radix-ui/themes";
import { useAIAssistantInstallation } from "hooks/aiAssistant/useAIAssistantInstallation";
import { AssistantStatus, AssistantLoadError } from "./AssistantStatus";

export default function AIAssistantInstallation() {
  const query = useAIAssistantInstallation();
  const [copyMessage, setCopyMessage] = useState("");
  if (query.isPending) return <Text role="status">Loading installation information…</Text>;
  if (query.isError) return <AssistantLoadError error={query.error} onRetry={query.refetch} />;
  const settings = query.data;
  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage(`${label} copied.`);
    } catch {
      setCopyMessage("Copy unavailable. Select the text to copy it manually.");
    }
  };
  return <Flex direction="column" gap="5" width="100%" maxWidth="720px">
    <Heading size="6">Installation</Heading>
    <Card><Flex direction="column" gap="5">
      <AssistantStatus settings={settings} />
      {!settings.ready ? <Flex direction="column" gap="3">
        <Heading size="4">Complete website setup</Heading>
        <Text>Installation is available when all requirements below are confirmed.</Text>
        <ul aria-label="Installation readiness">{settings.readiness?.map(item => <li key={item.key}>
          {item.ready ? "✓" : "○"} {item.label} — {item.ready ? "Ready" : "Required"}
        </li>)}</ul>
        <Link to="/ai-assistant/settings">AI Assistant Settings</Link>
        <Link to="/ai-assistant/website">Website settings</Link>
      </Flex> : <Flex direction="column" gap="3">
        <Heading size="4">Installation script</Heading>
        <Text size="2">Add this script to an approved website. The widget creates a secure session when the website loads.</Text>
        <pre style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", padding: 16, background: "var(--gray-3)", borderRadius: 8 }}><code>{settings.snippet}</code></pre>
        <Button onClick={() => copy(settings.snippet, "Installation script")}>Copy script</Button>
      </Flex>}
      <Flex direction="column" gap="2"><Heading size="3">Allowed websites</Heading>
        {settings.allowed_origins?.length ? <ul>{settings.allowed_origins.map(origin => <li key={origin} style={{ overflowWrap: "anywhere" }}>{origin}</li>)}</ul> : <Text color="gray">No approved websites configured.</Text>}
      </Flex>
      <Flex direction="column" gap="3">
        <Heading size="3">Public Assistant ID</Heading>
        {settings.public_assistant_id ? <>
          <Text as="p" style={{ overflowWrap: "anywhere", fontFamily: "monospace", userSelect: "text" }}>{settings.public_assistant_id}</Text>
          <Button variant="soft" onClick={() => copy(settings.public_assistant_id, "Public Assistant ID")}>Copy public ID</Button>
        </> : <Text color="gray">Enable AI Assistant in Settings to provision your organisation’s public Assistant ID.</Text>}
        <Text size="2" color="gray">This is a public identifier, not a secret.</Text>
      </Flex>
      {copyMessage && <Text role="status">{copyMessage}</Text>}
      {(settings.last_sync_error || settings.website_last_sync_error) && <Callout.Root color="red" role="alert"><Callout.Text>{settings.website_last_sync_error || settings.last_sync_error}</Callout.Text></Callout.Root>}
      <Button variant="soft" onClick={query.refetch}>Refresh installation status</Button>
    </Flex></Card>
  </Flex>;
}
